import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Icon, Modal, Confirm } from 'semantic-ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faBook } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import academyService from 'src/services/api/academyService';
import CourseForm from './creation/CourseForm';
import LessonForm from './creation/LessonForm';
import AGTable from 'src/components/table/aggrid';
import { getCourseColumns, getLessonColumns } from './columns';
import { screenSizes } from 'src/assets/styles/Literals';

const DashboardStyles = styled.div`
  padding: 2rem;
  background-color: #f8f9fa;
  min-height: 100vh;
  width: 100vw;

  @media only screen and (min-width: 1500px) {
    width: 80vw;
  }

  .dashboard {
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #e9ecef;

      .title {
        font-family: 'Raleway', sans-serif;
        color: #2c3e50;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 1rem;
      }
    }

    .stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;

      .stat-card {
        background: white;
        padding: 1.5rem;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        border-left: 4px solid #007bff;

        .number {
          font-size: 2rem;
          font-weight: bold;
          color: #007bff;
          margin-bottom: 0.5rem;
        }

        .label {
          color: #6c757d;
          font-size: 0.9rem;
        }
      }
    }

    .content-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      overflow: hidden;

      .section-header {
        padding: 1.5rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .section-title {
          font-family: 'Raleway', sans-serif;
          color: #2c3e50;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .instruction {
          color: #6c757d;
          font-style: italic;
        }
      }

      .table-container {
        height: 60vh;
        width: 100%;
      }

      .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #6c757d;
        font-size: 1.1rem;
        text-align: center;
        padding: 2rem;

        .content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .text {
          margin: 0;
          font-size: 1rem;
          line-height: 1.5;
        }
      }
    }

    .lessons-section {
      margin-top: 2rem;
    }
  }
`;

const AcademyAdminDashboard = () => {
  const history = useHistory();
  const [courses, setCourses] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  // const [loading, setLoading] = useState(true);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [lessonModalOpen, setLessonModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // AG Grid refs
  const courseGridRef = useRef(null);
  const lessonGridRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // setLoading(true);
      const coursesResponse = await academyService.getAcademyCourses();
      const coursesData = coursesResponse.data?.AcademyCourses || [];
      setCourses(coursesData);

      // No longer need to extract all lessons since we only show course-specific lessons
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally {
      // setLoading(false);
    }
  };

  const handleCreateCourse = () => {
    setEditingCourse(null);
    setCourseModalOpen(true);
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseModalOpen(true);
  };

  // const handleCreateLesson = () => {
  //   setEditingLesson(null);
  //   setLessonModalOpen(true);
  // };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonModalOpen(true);
  };

  const handleFullEditor = (lesson) => {
    // Find the course that contains this lesson
    const course = courses.find((c) => c.AcademyLessons?.some((l) => l.id === lesson.id));
    if (course) {
      history.push(`/admin/crecerplus/lesson/${course.id}/${lesson.id}`);
    } else {
      Swal.fire('Error', 'No se pudo encontrar el curso para esta lección', 'error');
    }
  };

  const handleViewLessons = (course) => {
    setSelectedCourse(course);
    const courseLessons = course.AcademyLessons || [];
    setFilteredLessons(courseLessons);
  };

  const handleCreateLessonForCourse = (course) => {
    history.push(`/admin/crecerplus/lesson/${course.id}/new`);
  };

  // Remove the back to all lessons functionality since we only want course-specific views

  const handleDelete = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteType === 'course') {
        await academyService.deleteCourse(itemToDelete.id);
        Swal.fire('Éxito', 'Curso eliminado correctamente', 'success');
      } else if (deleteType === 'lesson') {
        await academyService.deleteLesson(itemToDelete.id);
        Swal.fire('Éxito', 'Lección eliminada correctamente', 'success');
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
      Swal.fire('Error', 'No se pudo eliminar el elemento', 'error');
    } finally {
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setDeleteType('');
    }
  };

  const handleCourseSave = async (courseData) => {
    try {
      if (editingCourse) {
        await academyService.updateCourse(editingCourse.id, courseData);
        Swal.fire('Éxito', 'Curso actualizado correctamente', 'success');
      } else {
        await academyService.createCourse(courseData);
        Swal.fire('Éxito', 'Curso creado correctamente', 'success');
      }
      setCourseModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving course:', error);
      Swal.fire('Error', 'No se pudo guardar el curso', 'error');
    }
  };

  const handleLessonSave = async (lessonData) => {
    try {
      if (editingLesson) {
        await academyService.updateLesson(editingLesson.id, lessonData);
        Swal.fire('Éxito', 'Lección actualizada correctamente', 'success');
      } else {
        await academyService.createLesson(lessonData);
        Swal.fire('Éxito', 'Lección creada correctamente', 'success');
      }
      setLessonModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving lesson:', error);
      Swal.fire('Error', 'No se pudo guardar la lección', 'error');
    }
  };

  // AG Grid event handlers
  const onCourseGridReady = (params) => {
    if (courseGridRef.current) {
      courseGridRef.current.api = params.api;
      courseGridRef.current.columnApi = params.columnApi;
      if (window.innerWidth > screenSizes.tablet) {
        params.api.sizeColumnsToFit();
      }
    }
  };

  const onLessonGridReady = (params) => {
    if (lessonGridRef.current) {
      lessonGridRef.current.api = params.api;
      lessonGridRef.current.columnApi = params.columnApi;
      if (window.innerWidth > screenSizes.tablet) {
        params.api.sizeColumnsToFit();
      }
    }
  };

  // Get column definitions
  const courseColumnDefs = getCourseColumns(
    handleEditCourse,
    handleDelete,
    handleViewLessons,
    handleCreateLessonForCourse
  );
  const lessonColumnDefs = getLessonColumns(
    courses,
    handleEditLesson,
    handleDelete,
    handleFullEditor,
    selectedCourse
  );

  const stats = {
    totalCourses: courses.length,
    totalLessons: courses.reduce((acc, course) => acc + (course.AcademyLessons?.length || 0), 0),
    activeCourses: courses.filter((course) => course.status !== false).length,
    completedLessons: courses.reduce(
      (acc, course) =>
        acc +
        (course.AcademyLessons?.filter((lesson) => lesson.status === 'completed')?.length || 0),
      0
    ),
  };

  return (
    <DashboardStyles>
      <div className="dashboard">
        <div className="header">
          <h1 className="title">
            <FontAwesomeIcon icon={faGraduationCap} color="#007bff" />
            Administración de Academia
          </h1>
        </div>

        <div className="stats">
          <div className="stat-card">
            <div className="number">{stats.totalCourses}</div>
            <div className="label">Total de Cursos</div>
          </div>
          <div className="stat-card">
            <div className="number">{stats.totalLessons}</div>
            <div className="label">Total de Lecciones</div>
          </div>
        </div>

        <div className="content-section">
          <div className="section-header">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faBook} />
              Cursos
            </h2>
            <div className="actions">
              <Button primary icon labelPosition="left" onClick={handleCreateCourse}>
                <Icon name="plus" />
                Crear Curso
              </Button>
            </div>
          </div>
          <div className="table-container">
            <AGTable
              columnDefs={courseColumnDefs}
              rowData={courses}
              tableRef={courseGridRef}
              onGridReady={onCourseGridReady}
              paginationPageSize={20}
              suppressDragLeaveHidesColumns={false}
              rowHeight={60}
            />
          </div>
        </div>

        <div className="content-section lessons-section">
          <div className="section-header">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faBook} />
              {selectedCourse
                ? `Lecciones de: ${selectedCourse.title}`
                : 'Selecciona un curso para ver sus lecciones'}
            </h2>
            <div className="actions">
              {selectedCourse ? (
                <Button
                  primary
                  icon
                  labelPosition="left"
                  onClick={() => handleCreateLessonForCourse(selectedCourse)}
                >
                  <Icon name="plus" />
                  Crear Lección para {selectedCourse.title}
                </Button>
              ) : (
                <div className="instruction">
                  Haz clic en "Ver Lecciones" en cualquier curso para ver sus lecciones
                </div>
              )}
            </div>
          </div>
          <div className="table-container">
            {selectedCourse ? (
              <AGTable
                columnDefs={lessonColumnDefs}
                rowData={filteredLessons}
                tableRef={lessonGridRef}
                onGridReady={onLessonGridReady}
                paginationPageSize={20}
                suppressDragLeaveHidesColumns={false}
                rowHeight={60}
              />
            ) : (
              <div className="empty-state">
                <div className="content">
                  <FontAwesomeIcon icon={faBook} className="icon" />
                  <p className="text">
                    Selecciona un curso de la tabla superior para ver sus lecciones
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Course Form Modal */}
        <Modal open={courseModalOpen} onClose={() => setCourseModalOpen(false)} size="large">
          <Modal.Header>{editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}</Modal.Header>
          <Modal.Content>
            <CourseForm
              course={editingCourse}
              onSave={handleCourseSave}
              onCancel={() => setCourseModalOpen(false)}
            />
          </Modal.Content>
        </Modal>

        {/* Lesson Form Modal */}
        <Modal open={lessonModalOpen} onClose={() => setLessonModalOpen(false)} size="large">
          <Modal.Header>{editingLesson ? 'Editar Lección' : 'Crear Nueva Lección'}</Modal.Header>
          <Modal.Content>
            <LessonForm
              lesson={editingLesson}
              courses={courses}
              onSave={handleLessonSave}
              onCancel={() => setLessonModalOpen(false)}
            />
          </Modal.Content>
        </Modal>

        {/* Delete Confirmation */}
        <Confirm
          open={deleteConfirmOpen}
          header="Confirmar Eliminación"
          content={`¿Estás seguro de que quieres eliminar este ${
            deleteType === 'course' ? 'curso' : 'lección'
          }? Esta acción no se puede deshacer.`}
          confirmButton="Eliminar"
          cancelButton="Cancelar"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirmOpen(false)}
        />
      </div>
    </DashboardStyles>
  );
};

export default AcademyAdminDashboard;
